import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import {
  computeSkinReference,
  heuristicHairOcclusionAnalyzer,
  insetRect,
} from '../face/hairOcclusion.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Hair-over-face check.
 *
 * Per-region coverage (both eyes, nose, mouth, cheeks) of "hair-like" pixels:
 * dark, low-saturation and locally flat compared with the measured skin patch.
 * The pixel logic lives behind `heuristicHairOcclusionAnalyzer`, whose `mode`
 * is reported in `details`, so a real segmentation mask can replace it without
 * touching this file.
 *
 * A hard failure is only possible for an eye region, with high coverage and a
 * confident measurement; everything else stays a warning for a human to look at.
 */
export const hairOcclusionCheck: ValidationCheck = {
  id: 'hair_occlusion',
  title: 'Волосы не закрывают глаза и лицо',
  description: 'Оценивает долю «похожих на волосы» пикселей в областях глаз, носа, рта и щёк.',
  kind: 'content',
  requires: ['pixels', 'geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'geometry']);
    if (missing) return notChecked(ctx, hairOcclusionCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    const pixels = ctx.pixels;
    if (!geometry || !pixels) {
      return notChecked(
        ctx,
        hairOcclusionCheck,
        'Нет данных для анализа перекрытия лица волосами.',
        ctx.rule.severity,
      );
    }

    // `minOcclusionArea` is the profile-facing alias of the warning floor.
    const warnFloor = numberParam(
      ctx.rule.params,
      'minOcclusionArea',
      numberParam(ctx.rule.params, 'minCoverage', 0.22),
    );
    const coverageLimit = numberParam(ctx.rule.params, 'maxCoverage', 0.45);
    const uncertainBelow = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.6);

    const skinReference = computeSkinReference(pixels, insetRect(geometry.faceBox, 0.18));
    const analysis = heuristicHairOcclusionAnalyzer.analyse(pixels, geometry, skinReference, {
      ...(typeof ctx.rule.params['hairDarkFactor'] === 'number'
        ? { darkFactor: ctx.rule.params['hairDarkFactor'] as number }
        : {}),
    });

    const worst = analysis.worst;
    const worstCoverage = worst?.coverage ?? 0;
    const eyeOffender = worst?.id === 'left_eye' || worst?.id === 'right_eye';
    const confident = analysis.confidence >= uncertainBelow;

    const status: CheckResult['status'] =
      worstCoverage < warnFloor
        ? 'pass'
        : eyeOffender && worstCoverage >= coverageLimit && confident
          ? 'fail'
          : confident
            ? 'warning'
            : 'uncertain';

    const message = (() => {
      if (status === 'pass') return '✅ Лицо свободно';
      if (status === 'fail') return '❌ Волосы частично закрывают глаз';
      if (status === 'uncertain') {
        return 'Не удалось надёжно оценить, закрывают ли волосы лицо.';
      }
      return eyeOffender
        ? '⚠ Возможное перекрытие области глаза'
        : '⚠ Возможное перекрытие области лица';
    })();

    const offenders = analysis.regions.filter((region) => region.coverage >= warnFloor);
    const shapes: NonNullable<CheckResult['geometry']> = offenders.map((region) => ({
      type: 'rect',
      coordinates: [
        roundTo(region.rect.x, 4),
        roundTo(region.rect.y, 4),
        roundTo(region.rect.width, 4),
        roundTo(region.rect.height, 4),
      ] as [number, number, number, number],
    }));

    return plainResult({
      id: hairOcclusionCheck.id,
      title: hairOcclusionCheck.title,
      status,
      severity: ctx.rule.severity,
      value: roundTo(worstCoverage, 4),
      max: coverageLimit,
      unit: 'ratio',
      ...(ctx.rule.official ? { official: ctx.rule.official } : {}),
      message,
      ...(ctx.rule.official?.statement ? { requirement: ctx.rule.official.statement } : {}),
      fixability: 'retake',
      geometry: shapes,
      problemRegions:
        status === 'pass'
          ? []
          : offenders.map((region) => ({
              id: `hair_occlusion_${region.id}`,
              rect: region.rect,
              message:
                region.id === 'left_eye' || region.id === 'right_eye'
                  ? 'Возможное перекрытие области глаза волосами.'
                  : 'Возможное перекрытие области лица волосами.',
              severity: status === 'fail' ? ('error' as const) : ('warning' as const),
            })),
      confidence: analysis.confidence,
      details: {
        regions: analysis.regions.map((region) => ({
          id: region.id,
          coverage: roundTo(region.coverage, 4),
          pixels: region.pixels,
          hairPixels: region.hairPixels,
          confidence: roundTo(region.confidence, 3),
          rect: region.rect,
        })),
        maxCoverage: roundTo(worstCoverage, 4),
        worstRegion: worst?.id ?? null,
        minCoverage: warnFloor,
        coverageLimit,
        uncertainBelowConfidence: uncertainBelow,
        mode: heuristicHairOcclusionAnalyzer.mode,
        skinReference,
        notes: analysis.notes,
        displayValue: status === 'pass' ? 'свободно' : 'возможное перекрытие',
      },
    });
  },
};
